/**
 * What the audit trail shows for each event: runs of routine events folded
 * into one row, and the metadata each row opens to.
 *
 * @module components/admin/audit-trail-rows
 */

import { actionFamily } from "@/components/shared/audit-target";
import type { AuditEvent } from "@/server/types";

/** Families that repeat as a matter of course and fold into one row. */
export const ROUTINE_FAMILIES = new Set(["impersonation", "login", "edit-lease"]);

/** Identifies one event among the trail's rows. */
export function auditEventKey(event: AuditEvent): string {
  return `${event.timestamp}-${event.actor}-${event.action}-${event.target}`;
}

/** One row of the trail: an event, or a run of routine events of one family. */
export type AuditRow =
  | { kind: "event"; key: string; event: AuditEvent }
  | { kind: "group"; key: string; family: string; events: readonly AuditEvent[] };

/**
 * Fold consecutive routine events of one family into a single row. A run of
 * one stays an event; nothing is reordered, so the trail stays newest first.
 */
export function groupAuditRows(events: readonly AuditEvent[]): AuditRow[] {
  const rows: AuditRow[] = [];
  let run: AuditEvent[] = [];
  const flush = (): void => {
    const [first] = run;
    if (first && run.length === 1) {
      rows.push({ kind: "event", key: auditEventKey(first), event: first });
    } else if (first) {
      rows.push({
        kind: "group",
        key: `group-${auditEventKey(first)}-${run.length}`,
        family: actionFamily(first.action),
        events: run,
      });
    }
    run = [];
  };
  events.forEach((event) => {
    const family = actionFamily(event.action);
    const [head] = run;
    if (head && actionFamily(head.action) !== family) {
      flush();
    }
    if (ROUTINE_FAMILIES.has(family)) {
      run.push(event);
      return;
    }
    flush();
    rows.push({ kind: "event", key: auditEventKey(event), event });
  });
  flush();
  return rows;
}

const FAMILY_LABELS: Record<string, string> = {
  impersonation: "Impersonation",
  login: "Sign-in",
  "edit-lease": "Edit lease",
};

/** "Sign-in events (4)" for a folded run. */
export function auditGroupLabel(row: Extract<AuditRow, { kind: "group" }>): string {
  return `${FAMILY_LABELS[row.family] ?? row.family} events (${row.events.length})`;
}

/** Metadata as label/value text, for the detail every row opens to. */
export function auditMetadataEntries(event: AuditEvent): { label: string; value: string }[] {
  return Object.entries(event.metadata ?? {})
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([label, value]) => ({
      label,
      value: typeof value === "string" ? value : JSON.stringify(value),
    }));
}
